/**
 * extraction.service.ts
 * ---------------------
 * Module 6 — Document Normalization & Text Extraction (frontend API service).
 *
 * Endpoints used:
 *   POST  /api/v1/sessions/{id}/extract            — trigger extraction
 *   GET   /api/v1/sessions/{id}/extraction-status  — poll status
 */

import { Injectable }                    from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError }        from 'rxjs';
import { catchError }                    from 'rxjs/operators';
import { environment }                   from '../../../environments/environment';

// ---------------------------------------------------------------------------
// DTOs — mirror backend Pydantic schemas
// ---------------------------------------------------------------------------

export interface ExtractedFileInfo {
  file_name:      string;
  total_pages:    number;
  total_chunks:   number;
  figures_found:  number;
  json_blob_path: string;
}

export interface ExtractionResponse {
  session_id:      string;
  status:          string;   // 'started'
  files_processed: number;
  results:         ExtractedFileInfo[];
}

export interface ExtractionStatusResponse {
  session_id:      string;
  extraction_done: boolean;
  // Rich progress fields
  status:          string;  // 'not_started' | 'running' | 'completed' | 'failed'
  progress:        number;  // 0–100
  message:         string;
  phase:           string;  // 'starting' | 'images' | 'documents' | 'metadata' | 'done' | 'failed' | 'waiting'
  completed_files: number;
  total_files:     number;
  current_file:    string;
  extracted_files: string[];
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class ExtractionService {

  private readonly _base =
    `${environment.apiConfig.uri.replace(/\/$/, '')}/api/v1/sessions`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Trigger document extraction for a session.
   * Returns immediately — poll getExtractionStatus() to track progress.
   */
  triggerExtraction(sessionId: string): Observable<ExtractionResponse> {
    return this.http
      .post<ExtractionResponse>(`${this._base}/${sessionId}/extract`, {})
      .pipe(catchError(this._handleError));
  }

  /**
   * Check whether extraction has completed for the session.
   */
  getExtractionStatus(sessionId: string): Observable<ExtractionStatusResponse> {
    return this.http
      .get<ExtractionStatusResponse>(`${this._base}/${sessionId}/extraction-status`)
      .pipe(catchError(this._handleError));
  }

  private _handleError(err: HttpErrorResponse): Observable<never> {
    const message =
      err.error?.detail ??
      err.error?.message ??
      err.message ??
      'An unknown error occurred.';
    return throwError(() => new Error(message));
  }
}
